'use client';

import React from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { useTimePilotT } from '@/i18n/useTimePilotT';

type DisplayMediaToggleProps = {
  /** True when the source already has a paired Displayed Media row. */
  isDisplayed: boolean;
  label: string;
  onToggle: () => void;
};

/**
 * Figma per-row media toggle. "Display Media" is a text button that surfaces a
 * rectangular hover affordance; once paired with Displayed Media it becomes the
 * filled "Hide Media" state. Same membership rules as the legacy +/- icons.
 */
export function DisplayMediaToggle({ isDisplayed, label, onToggle }: DisplayMediaToggleProps) {
  const t = useTimePilotT();
  return (
    <TpTooltip
      content={isDisplayed ? t('sources.removeFromDisplayed') : t('sources.showInDisplayed')}
      placement="top"
      delayDuration={0}
    >
      <Button
        type="button"
        size="sm"
        style={isDisplayed ? 'solid' : 'text'}
        color={isDisplayed ? 'secondary' : 'light'}
        background="inverse"
        aria-pressed={isDisplayed}
        aria-label={t(isDisplayed ? 'sources.hideFor' : 'sources.displayFor', { label })}
        data-testid="display-media-toggle"
        className="shrink-0 whitespace-nowrap rounded-[4px]"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {isDisplayed ? t('sources.hideMedia') : t('sources.displayMedia')}
      </Button>
    </TpTooltip>
  );
}
